import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { Layer } from 'types'
import { layersKeys } from 'hooks/queries/layers/layersKeys'
import { buildErrorMessage } from 'utils'
import { useArticlePassword } from 'hooks/useArticlePassword'
import uniqBy from 'lodash/uniqBy'
import { UseMutationOptions } from 'react-query/types/react/types'

interface Props {
  articleKey: string
}

export interface Data {
  layer: Layer
}

export interface Variables {
  name?: string
  isPublic?: boolean
  userAccesses?: {
    id?: number
    email_address?: string
  }[]
  guestAccesses?: {
    email_address: string
  }[]
  groupAccesses?: {
    id: number
  }[]
}

export const useCreateLayer = (
  { articleKey }: Props,
  options?: Omit<UseMutationOptions<Data, unknown, Variables>, 'mutationKey' | 'mutationFn'>,
) => {
  const queryClient = useQueryClient()
  const password = useArticlePassword(articleKey)

  return useMutation<Data, unknown, Variables>(
    'createLayer',
    async ({ name, isPublic = true, userAccesses, guestAccesses, groupAccesses }) => {
      const response = await sendToApi<{ layer: Layer }>(`/articles/${articleKey}/layers`, {
        method: 'POST',
        body: {
          name,
          public: isPublic,
          user_accesses: userAccesses,
          guest_accesses: guestAccesses,
          group_accesses: groupAccesses,
        },
      })
      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error(
          buildErrorMessage(response.data.errors) ||
            'An error occurred! Our team has been notified and we are looking into it.',
        )
      }
      return response.data
    },
    {
      ...options,
      onSuccess: async (data, variables, context) => {
        queryClient.setQueryData(
          layersKeys.list(articleKey, { password }),
          (existingData: { layers: Layer[] } | undefined) => {
            const { layers: oldLayers = [] } = existingData || { layers: [] }
            return {
              layers: uniqBy([...oldLayers, data.layer], 'id'),
            }
          },
        )
        options?.onSuccess?.(data, variables, context)
      },
    },
  )
}
